import { useEffect, useState } from 'react';
import HTMLParser from 'html-react-parser';
import { useNavigate } from 'react-router-dom';
import { IApiContentResponseModel, MeDto } from '@/services/platform/api/model';
import { AppProfilePropertiesModel } from '@/services/platform/api/model';
import { useApp } from '@/lib/core/providers/appProvider';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { logout } from '@/services/platform/data/authentication';
import { cn, ImagesToImageMap } from '@/lib/core/utils';
import { ModulesRow } from '@/components/modules/row';
import { useClientTranslation } from '@/lib/core';
import { ConfirmationSheet } from '@/components/shared/sheets/confirmation';
import { Button } from '@/components/ui/button';
import { ScreenHead } from '@/components/shared/screen/head';
import { AppContext } from '@/lib/core/types';
import { getUserProfile } from '@/services/platform/data/profile';

interface ProfileIntroProps {
  content: IApiContentResponseModel;
  context: AppContext | undefined;
}

export function ProfileIntro({ content, context }: ProfileIntroProps) {
  const navigate = useNavigate();
  const translation = useClientTranslation();
  const { user, profile: cachedProfile, membership, appSettings, memberTheme } = useApp();
  const { setMainClassName } = useUIStore();

  const [profile, setProfile] = useState<MeDto | undefined>(cachedProfile);

  useEffect(() => {
    getUserProfile()
      .then(data => setProfile(data))
      .catch(() => setProfile(cachedProfile));
  }, []);

  const hasActiveMembership = user?.hasActiveMembership;

  useEffect(() => {
    setMainClassName('!pb-0');
    return () => setMainClassName('');
  }, [setMainClassName]);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);

  const properties = content.properties as AppProfilePropertiesModel;

  const heroUrl = ImagesToImageMap(properties.pageHero, 'profileHero')?.src;
  const nonMemberImage = ImagesToImageMap(properties.nonMembersJNcQuoi, 'nonMembersJNcQuoi');
  const photoUrl = profile?.photoUrl;
  const defaultAvatarUrl = ImagesToImageMap(
    appSettings?.properties?.profileDefaultImage,
    'profileDefaultImg'
  )?.src;
  const avatarSrc = photoUrl && photoUrl?.includes('http') ? photoUrl : defaultAvatarUrl || '';
  const memberName = profile?.name ?? '';
  const memberSinceYear = membership?.startDate ? new Date(membership.startDate).getFullYear() : '';

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-dvh" style={hasActiveMembership ? { background: memberTheme.pageBg } : undefined}>
      {hasActiveMembership && (
        <>
          {/* ── Hero ──────────────────────────────────────────────────────── */}
          <div className="relative overflow-hidden">
            {heroUrl && (
              <img
                src={heroUrl}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full scale-110 object-cover blur-[1.5px]"
              />
            )}
            <div
              className="relative z-10 flex flex-col items-center px-4 pb-10"
              style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
            >
              {/* Top bar */}
              <ScreenHead
                variant="overlay"
                title={properties.pageName ?? content.name ?? ''}
                onBack={() => navigate(-1)}
                ariaLabel={translation('App.Labels.GoBack')}
                className="px-0"
              />

              {/* Avatar */}
              <div className="mt-4">
                {avatarSrc ? (
                  <img src={avatarSrc} alt={memberName} className="size-30 rounded-full object-cover" />
                ) : (
                  <div className="size-30 rounded-full bg-white/20" />
                )}
              </div>

              {/* Welcome text */}
              <p className="font-didot mt-4 text-center text-[24px] leading-none tracking-[-0.48px] text-white italic">
                {properties.welcome ?? translation('App.Labels.WelcomeBack')}
              </p>

              {/* Member name — `text-center` matters only once it wraps: `items-center` centres the
                  box, but without this the second line of a long name sits flush left inside it. */}
              <p className="font-pp-neue-montreal mt-1 text-center text-[28px] leading-none font-semibold tracking-[0.5px] text-white uppercase">
                {memberName}
              </p>

              {/* Membership badge */}
              {(membership?.type || memberSinceYear) && (
                <div className="mt-3 px-3 py-1">
                  <p className="font-pp-neue-montreal text-center text-[12px] leading-none tracking-[0.5px] text-white uppercase">
                    {membership?.type && <span className="font-semibold">{membership.type}</span>}
                    {membership?.type && memberSinceYear && <span className="font-normal"> | </span>}
                    {memberSinceYear && (
                      <span className="font-normal">
                        {translation('App.Labels.MemberSince')} {memberSinceYear}
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Non-member layout ─────────────────────────────────────────── */}
      {!hasActiveMembership && (
        <>
          <div className="w-full">
            <div className="pt-safe-area!">
              <ScreenHead
                title={properties.pageName ?? content.name ?? ''}
                onBack={() => navigate(-1)}
                ariaLabel={translation('App.Labels.GoBack')}
                className="pt-0"
              />
            </div>
          </div>

          <div className="flex flex-col items-start gap-6 px-4 pb-8">
            {/* Banner */}
            <div className="flex w-full items-center gap-2 pt-4">
              <div className="flex min-w-0 flex-1 flex-col gap-2 leading-none">
                <p className="font-didot text-jncquoi-neutral-1000 text-[24px] leading-none font-normal tracking-[-0.48px] italic">
                  {properties.welcome ?? translation('App.Labels.WelcomeBack')}
                </p>
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[28px] leading-none font-semibold tracking-[0.5px] uppercase">
                  {memberName}
                </p>
              </div>
            </div>

            {/* Membership info card */}
            <div className="border-jncquoi-neutral-200 flex w-full items-center gap-4 overflow-hidden border-solid bg-white pl-4">
              <div className="flex min-w-0 flex-1 items-center gap-2.5 py-4">
                <div className="font-pp-neue-montreal flex min-w-0 flex-1 flex-col items-start gap-0.5 text-[12px] leading-normal tracking-[0.5px]">
                  {properties.nonMemberText?.markup
                    ? HTMLParser(properties.nonMemberText.markup)
                    : translation('App.Labels.NonMemberText')}
                </div>
              </div>
              {nonMemberImage && (
                <div className="flex items-center self-stretch">
                  <div className="flex h-full w-22 flex-col items-center justify-center">
                    <img
                      src={nonMemberImage?.src}
                      alt={nonMemberImage?.alt}
                      aria-hidden="true"
                      className="max-w-full"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Content ───────────────────────────────────────────────────── */}
      <div
        className={cn(
          'pb-[calc(((env(safe-area-inset-bottom)+1.5rem)*2)+96px)]',
          hasActiveMembership ? 'pt-8' : 'border-jncquoi-neutral-200 pt-0'
        )}
      >
        <ModulesRow components={properties.components} context={context} />

        {/* Sign Out */}
        <div className="mx-4 mt-20">
          <Button
            variant="secondary-outline"
            palette="dark"
            className="w-full"
            onClick={() => setIsLogoutOpen(true)}
          >
            {translation('App.Labels.SignOut')}
          </Button>
        </div>
      </div>

      {/* ── Logout confirmation sheet ──────────────────────────────────── */}
      <ConfirmationSheet
        open={isLogoutOpen}
        onOpenChange={setIsLogoutOpen}
        onConfirm={handleLogout}
        headerTitle={translation('App.Labels.Confirmation')}
        title={translation('App.Labels.LogoutConfirmQuestion')}
        confirmLabel={translation('App.Labels.LogoutAction')}
        dismissLabel={translation('App.Labels.StayLoggedIn')}
      />
    </div>
  );
}
